import React, { useState, useEffect, Suspense, lazy } from "react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Users, CreditCard, Calendar, BarChart3, LogOut, Clock, Settings, Eye, Info, Wallet, LogIn, AlertTriangle, ShieldCheck, Loader2, HeartPulse, Cable } from "lucide-react";
import { useAuth } from "../context/AuthContext";
const Dashboard = lazy(() => import("./Dashboard"));
const Members = lazy(() => import("./Members"));
const CheckIn = lazy(() => import("./CheckIn"));
const Subscriptions = lazy(() => import("./Subscriptions"));
const HistoryReports = lazy(() => import("./HistoryReports"));
const AccessControl = lazy(() => import("./AccessControl"));
const UsersManagement = lazy(() => import("./UsersManagement"));
const About = lazy(() => import("./About"));
const Expenses = lazy(() => import("./Expenses"));
const ResetData = lazy(() => import("./ResetData"));
const HealthJourney = lazy(() => import("./HealthJourney"));
const EquipmentsManagement = lazy(() => import("./EquipmentsManagement"));
import SyncStatusBadge from "../components/SyncStatusBadge";
import { GuidedTour } from "../components/GuidedTour";
import TwoFactorSettings from "../components/TwoFactorSettings";
import { APP_VERSION, BUILD_DATE, NEW_TABS } from "../version";
import { toast } from "sonner";
import { apiCall } from "../lib/api";
import { LanguageSelector } from "../components/LanguageSelector";
import { useLanguage } from "../i18n/LanguageContext";

const NewTabBadge = () => (
  <span
    className="ml-1 text-[9px] font-bold text-white bg-red-500 px-1.5 py-0.5 rounded-full animate-pulse"
    data-testid="new-tab-badge"
  >
    Nouveau
  </span>
);

const MainApp = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [show2FA, setShow2FA] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [seenNewTabs, setSeenNewTabs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('new_tabs_seen_' + APP_VERSION) || '[]'); } catch (e) { return []; }
  });

  const isNewTab = (v) => NEW_TABS.includes(v) && !seenNewTabs.includes(v);
  const markTabSeen = (v) => {
    if (NEW_TABS.includes(v) && !seenNewTabs.includes(v)) {
      const next = [...seenNewTabs, v];
      setSeenNewTabs(next);
      try { localStorage.setItem('new_tabs_seen_' + APP_VERSION, JSON.stringify(next)); } catch (e) {}
    }
  };
  const onTabChange = (v) => { setActiveTab(v); markTabSeen(v); };

  const isAdmin = user?.role === 'admin';
  const isPrimaryAdmin = isAdmin && user?.username === 'admin';
  const tourSteps = [
    { value: 'dashboard', selector: "[data-testid='tab-dashboard']", title: 'Tableau de bord', text: "Vue d'ensemble : statistiques, rappels d'expiration et pilotage des relances." },
    { value: 'members', selector: "[data-testid='tab-members']", title: 'Membres', text: 'Ajoutez, importez et gérez vos adhérents, avec fiches détaillées et détection des doublons.' },
    { value: 'checkin', selector: "[data-testid='tab-checkin']", title: 'Entrées / Sorties', text: 'Pointez les arrivées et départs des membres au quotidien.' },
    { value: 'subscriptions', selector: "[data-testid='tab-subscriptions']", title: 'Abonnements', text: 'Créez les abonnements, enregistrez les paiements et générez les reçus.' },
    { value: 'health', selector: "[data-testid='tab-health']", title: 'Parcours Santé', text: "Suivez les objectifs santé de vos abonnés : poids, IMC, points de contrôle et bilans avec coach IA." },
    { value: 'history', selector: "[data-testid='tab-history']", title: 'Historique', text: "Consultez l'historique des accès et des connexions, avec exports." },
    ...(isAdmin ? [
      { value: 'access', selector: "[data-testid='tab-access']", title: "Contrôle d'accès", text: "Suivez les journaux d'accès des membres (réservé aux administrateurs)." },
      { value: 'users', selector: "[data-testid='tab-users']", title: 'Utilisateurs', text: 'Gérez les comptes du personnel : création, mot de passe, activation.' },
      { value: 'about', selector: "[data-testid='tab-about']", title: 'À propos', text: 'Retrouvez ce guide complet à tout moment, et relancez la visite depuis ici.' },
    ] : []),
  ];

  useEffect(() => {
    if (!localStorage.getItem('tour_done')) {
      const t = setTimeout(() => setRunTour(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const handler = () => setRunTour(true);
    window.addEventListener('racinefit:start-tour', handler);
    return () => window.removeEventListener('racinefit:start-tour', handler);
  }, []);

  const endTour = () => {
    localStorage.setItem('tour_done', '1');
    setRunTour(false);
  };

  // Notifications dépenses : admin (nouvelles demandes) & staff (validation/refus)
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    const notify = (title, body) => {
      toast.info(`${title} — ${body}`, { duration: 8000 });
      if ('Notification' in window) {
        const show = () => { try { new Notification(title, { body, icon: '/icon-192x192.svg' }); } catch (e) {} };
        if (Notification.permission === 'granted') show();
        else if (Notification.permission !== 'denied') Notification.requestPermission().then((p) => { if (p === 'granted') show(); });
      }
    };
    const poll = async () => {
      try {
        const list = await apiCall('/expenses');
        if (!Array.isArray(list)) return;
        if (isAdmin) {
          const pending = list.filter((e) => e.status === 'pending');
          setPendingCount(pending.length);
          let seen = [];
          try { seen = JSON.parse(localStorage.getItem('exp_notif_seen_pending') || '[]'); } catch (e) {}
          const fresh = pending.filter((e) => !seen.includes(e.id));
          if (seen.length > 0) {
            fresh.forEach((e) => notify('Nouvelle demande de dépense', `${(e.amount || 0).toLocaleString()} CFA · ${e.category} · par ${e.requested_by_name || e.requested_by}`));
          }
          localStorage.setItem('exp_notif_seen_pending', JSON.stringify(pending.map((e) => e.id)));
        } else {
          const mine = list.filter((e) => e.requested_by === user?.username);
          let prev = {};
          try { prev = JSON.parse(localStorage.getItem('exp_notif_status_map') || '{}'); } catch (e) {}
          const hadPrev = Object.keys(prev).length > 0;
          mine.forEach((e) => {
            if (hadPrev && prev[e.id] && prev[e.id] !== e.status) {
              if (e.status === 'approved') notify('Dépense approuvée ✅', `${(e.amount || 0).toLocaleString()} CFA · ${e.category}`);
              else if (e.status === 'rejected') notify('Dépense refusée ❌', `${(e.amount || 0).toLocaleString()} CFA · ${e.category}${e.rejection_reason ? ` · ${e.rejection_reason}` : ''}`);
            }
          });
          const map = {}; mine.forEach((e) => { map[e.id] = e.status; });
          localStorage.setItem('exp_notif_status_map', JSON.stringify(map));
        }
      } catch (e) { /* silencieux */ }
    };
    poll();
    const t = setInterval(poll, 60000);
    return () => clearInterval(t);
  }, [isAdmin, user?.username]);

  // Verrou d'accès : si la salle n'est pas ouverte, les non-admins ne peuvent pas utiliser l'app
  const [gymOpen, setGymOpen] = useState(null); // null = inconnu
  useEffect(() => {
    let active = true;
    const check = async () => {
      // Hors-ligne : ne PAS interroger le serveur (renverrait un état vide => re-verrouillage à tort).
      // On conserve le dernier état connu, mémorisé localement (permet de rester déverrouillé après une ouverture hors-ligne).
      if (!navigator.onLine) {
        const local = localStorage.getItem('gym_open_local');
        // Inconnu hors-ligne => considérer la salle FERMÉE pour proposer l'ouverture (workflow préservé)
        if (active) setGymOpen(local !== null ? local === 'true' : false);
        return;
      }
      try {
        const data = await apiCall('/gym/today');
        const open = !!data?.is_open;
        if (active) setGymOpen(open);
        localStorage.setItem('gym_open_local', String(open));
      } catch (e) {
        // Erreur réseau : garder le dernier état local connu
        const local = localStorage.getItem('gym_open_local');
        if (active && local !== null) setGymOpen(local === 'true');
      }
    };
    check();
    const t = setInterval(check, 30000);
    return () => { active = false; clearInterval(t); };
  }, []);

  const gymLocked = !isAdmin && gymOpen === false;

  // Ouverture de la salle depuis l'écran restreint (staff) — seule action disponible salle fermée.
  // Fonctionne aussi HORS-LIGNE : déverrouillage optimiste immédiat + mise en file pour synchronisation.
  const [openingGym, setOpeningGym] = useState(false);
  const unlockLocally = (offline) => {
    setGymOpen(true);
    localStorage.setItem('gym_open_local', 'true');
    toast.success(offline
      ? 'Salle ouverte en mode hors-ligne — synchronisation au retour du réseau.'
      : 'Salle ouverte — bienvenue !');
  };
  const openGymFromLock = async () => {
    setOpeningGym(true);
    try {
      if (!navigator.onLine) {
        // Hors-ligne : la requête est mise en file par apiCall, on déverrouille immédiatement
        await apiCall('/gym/open', { method: 'POST' });
        unlockLocally(true);
        return;
      }
      const res = await apiCall('/gym/open', { method: 'POST' });
      if (res?.is_open || res?.success) {
        unlockLocally(false);
      } else {
        toast.error("La salle n'a pas pu être ouverte. Réessayez.");
      }
    } catch (e) {
      // Coupure réseau pendant la requête : déverrouillage optimiste hors-ligne (la requête est en file)
      unlockLocally(true);
    } finally {
      setOpeningGym(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <TwoFactorSettings open={show2FA} onOpenChange={setShow2FA} />
      <GuidedTour
        steps={tourSteps}
        run={runTour && !gymLocked}
        onStepChange={(s) => setActiveTab(s.value)}
        onFinish={endTour}
        onSkip={endTour}
      />
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-center gap-2 py-3 sm:h-16 sm:flex-row sm:justify-between sm:gap-0 sm:py-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-orange-800">Racinefit</h1>
              <span
                className="text-[10px] font-medium text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-full"
                data-testid="app-version-badge"
                title={`Version ${APP_VERSION}${BUILD_DATE ? ` — build ${BUILD_DATE}` : ''}`}
              >
                {APP_VERSION}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              <SyncStatusBadge />
              <LanguageSelector />
              <span className="max-w-[130px] truncate text-xs text-gray-700 sm:max-w-none sm:text-sm" data-testid="current-user-greeting">{t("Bonjour")}, <span data-no-translate>{user?.full_name}</span></span>
              <Badge variant="secondary" data-testid="current-user-role">{user?.role}</Badge>
              <Button variant="outline" size="sm" className="shrink-0 text-xs sm:text-sm" onClick={() => setShow2FA(true)} data-testid="open-2fa-settings-btn">
                <ShieldCheck className="w-4 h-4 mr-2" />
                Sécurité (2FA)
              </Button>
              <Button variant="outline" size="sm" className="shrink-0 text-xs sm:text-sm" onClick={logout} data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto">
        {gymLocked ? (
          <div className="flex flex-col items-center justify-center text-center py-24 px-6" data-testid="gym-locked-screen">
            <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center mb-6">
              <Clock className="w-10 h-10 text-orange-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">La salle est fermée</h2>
            <p className="text-gray-500 max-w-md mb-8">
              Ouvrez la salle pour accéder à l'ensemble de l'application. Tant que la salle est fermée, seule cette action est disponible.
            </p>
            <Button
              size="lg"
              onClick={openGymFromLock}
              disabled={openingGym}
              className="bg-green-600 hover:bg-green-700 text-white text-lg font-semibold px-10 py-6 rounded-2xl shadow-lg transition-colors"
              data-testid="gym-open-locked-button"
            >
              <LogIn className="w-6 h-6 mr-3" />
              {openingGym ? 'Ouverture…' : 'Ouvrir la salle'}
            </Button>
            <p className="text-xs text-gray-400 mt-6">L'application se débloque automatiquement dès l'ouverture de la salle.</p>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
          <TabsList className="grid h-auto w-full grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 justify-start rounded-none bg-white border-b">
            <TabsTrigger value="dashboard" className="min-w-0 justify-start gap-2 whitespace-normal text-left" data-testid="tab-dashboard">
              <BarChart3 className="w-4 h-4" />
              <span>Tableau de bord</span>
              {isNewTab('dashboard') && <NewTabBadge />}
            </TabsTrigger>
            <TabsTrigger value="members" className="min-w-0 justify-start gap-2 whitespace-normal text-left" data-testid="tab-members">
              <Users className="w-4 h-4" />
              <span>Membres</span>
              {isNewTab('members') && <NewTabBadge />}
            </TabsTrigger>
            <TabsTrigger value="checkin" className="min-w-0 justify-start gap-2 whitespace-normal text-left" data-testid="tab-checkin">
              <Clock className="w-4 h-4" />
              <span>Entrées/Sorties</span>
              {isNewTab('checkin') && <NewTabBadge />}
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="min-w-0 justify-start gap-2 whitespace-normal text-left" data-testid="tab-subscriptions">
              <CreditCard className="w-4 h-4" />
              <span>Abonnements</span>
              {isNewTab('subscriptions') && <NewTabBadge />}
            </TabsTrigger>
            <TabsTrigger value="health" className="min-w-0 justify-start gap-2 whitespace-normal text-left" data-testid="tab-health">
              <HeartPulse className="w-4 h-4" />
              <span>Parcours Santé</span>
              {isNewTab('health') && <NewTabBadge />}
            </TabsTrigger>
            <TabsTrigger value="history" className="min-w-0 justify-start gap-2 whitespace-normal text-left" data-testid="tab-history">
              <Calendar className="w-4 h-4" />
              <span>Historique</span>
              {isNewTab('history') && <NewTabBadge />}
            </TabsTrigger>
            <TabsTrigger value="expenses" className="min-w-0 justify-start gap-2 whitespace-normal text-left" data-testid="tab-expenses">
              <Wallet className="w-4 h-4" />
              <span>Dépenses</span>
              {isAdmin && pendingCount > 0 && (
                <span className="ml-1 text-[9px] font-bold text-white bg-amber-500 px-1.5 py-0.5 rounded-full" data-testid="expenses-pending-badge">{pendingCount}</span>
              )}
              {isNewTab('expenses') && <NewTabBadge />}
            </TabsTrigger>
            {user?.role === 'admin' && (
              <>
                <TabsTrigger value="access" className="min-w-0 justify-start gap-2 whitespace-normal text-left" data-testid="tab-access">
                  <Eye className="w-4 h-4" />
                  <span>Contrôle d'accès</span>
                  {isNewTab('access') && <NewTabBadge />}
                </TabsTrigger>
                <TabsTrigger value="users" className="min-w-0 justify-start gap-2 whitespace-normal text-left" data-testid="tab-users">
                  <Settings className="w-4 h-4" />
                  <span>Utilisateurs</span>
                  {isNewTab('users') && <NewTabBadge />}
                </TabsTrigger>
                <TabsTrigger value="about" className="min-w-0 justify-start gap-2 whitespace-normal text-left" data-testid="tab-about">
                  <Info className="w-4 h-4" />
                  <span>À propos</span>
                  {isNewTab('about') && <NewTabBadge />}
                </TabsTrigger>
                {isPrimaryAdmin && (
                  <TabsTrigger value="equipments" className="min-w-0 justify-start gap-2 whitespace-normal text-left" data-testid="tab-equipments">
                    <Cable className="w-4 h-4" />
                    <span>Équipements RJ45</span>
                  </TabsTrigger>
                )}
                {isPrimaryAdmin && (
                  <TabsTrigger value="reset" className="min-w-0 justify-start gap-2 whitespace-normal text-left text-red-600" data-testid="tab-reset">
                    <AlertTriangle className="w-4 h-4" />
                    <span>Remise à zéro</span>
                    {isNewTab('reset') && <NewTabBadge />}
                  </TabsTrigger>
                )}
              </>
            )}
          </TabsList>

          <Suspense fallback={<div className="flex items-center justify-center py-24" data-testid="page-loading"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>}>
          <TabsContent value="dashboard" className="mt-0">
            <Dashboard />
          </TabsContent>
          
          <TabsContent value="members" className="mt-0">
            <Members />
          </TabsContent>
          
          <TabsContent value="checkin" className="mt-0">
            <CheckIn />
          </TabsContent>
          
          <TabsContent value="subscriptions" className="mt-0">
            <Subscriptions />
          </TabsContent>
          
          <TabsContent value="health" className="mt-0">
            <HealthJourney />
          </TabsContent>

          <TabsContent value="history" className="mt-0">
            <HistoryReports />
          </TabsContent>

          <TabsContent value="expenses" className="mt-0">
            <Expenses />
          </TabsContent>
          
          {user?.role === 'admin' && (
            <>
              <TabsContent value="access" className="mt-0">
                <AccessControl />
              </TabsContent>
              <TabsContent value="users" className="mt-0">
                <UsersManagement />
              </TabsContent>
              <TabsContent value="about" className="mt-0">
                <About />
              </TabsContent>
              {isPrimaryAdmin && (
                <TabsContent value="equipments" className="mt-0">
                  <EquipmentsManagement />
                </TabsContent>
              )}
              {isPrimaryAdmin && (
                <TabsContent value="reset" className="mt-0">
                  <ResetData />
                </TabsContent>
              )}
            </>
          )}
          </Suspense>
        </Tabs>
        )}
      </div>
    </div>
  );
};


export default MainApp;
